import { useQuery } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import type { ResponseArray } from '@/types/shared';

/**
 * Customer revenue allocation tab — `GET /reports/bi/revenue-combined/customer/:id`
 * (BI surface, DataCentral v4). Params include gte/lte (YYYY-MM month range),
 * page/page_size/sort_by/sort_order for the detail flat list.
 *
 * API shape confirmed via probe:
 * - `results` — array of RevenueCombinedRow (null when no pagination params; [] when paginated)
 * - `pagination` — standard envelope
 * - `summary` — RevenueCombinedSummary (always present, even when results are empty)
 */

export interface RevenueCombinedRow {
  id?: number;
  customer_id?: number;
  customer_name?: string;
  service_id?: number;
  service_name?: string;
  product_group_name?: string;
  revenue_month?: string;
  total_amount?: number;
  allocated_amount?: number;
  revenue_amount?: number;
  is_projected?: boolean;
  is_paid?: boolean;
  is_allocated?: boolean;
  is_from_previous_year?: boolean;
  invoice_id?: number;
  revenue_type?: string;
  payment_date?: string;
}

/** Summary block — always returned, even for empty results. */
export interface RevenueCombinedSummary {
  total_records?: number;
  total_actual_records?: number;
  total_projected_records?: number;
  total_allocated_amount?: number;
  total_actual_amount?: number;
  total_projected_amount?: number;
  total_paid_records?: number;
  total_unpaid_records?: number;
  total_paid_amount?: number;
  total_unpaid_amount?: number;
  total_recognized_records?: number;
  total_unrecognized_records?: number;
  total_recognized_amount?: number;
  total_unrecognized_amount?: number;
  total_by_invoice_id?: number;
  total_by_customer_id?: number;
  total_by_service_id?: number;
}

/** Raw API envelope: standard `results`/`pagination` + the `summary` side-car. */
interface RevenueCombinedEnvelope extends ResponseArray<RevenueCombinedRow> {
  summary?: RevenueCombinedSummary;
}

export interface RevenueCombinedParams {
  gte?: string; // YYYY-MM
  lte?: string; // YYYY-MM
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const q: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== '') q[k] = v;
  }
  return qs.stringify(q);
}

/** Combined revenue list + summary for one customer over a month range. */
export function useCustomerRevenueCombined(
  customerId: number,
  params: RevenueCombinedParams,
) {
  return useQuery({
    queryKey: ['customers', 'revenue-combined', customerId, params],
    enabled: !!customerId && !!params.gte && !!params.lte,
    queryFn: async (): Promise<RevenueCombinedEnvelope> => {
      const merged: Record<string, string | number | boolean | undefined> = {
        page: 1,
        page_size: 50,
        ...params,
      };
      const res = await dataCentralApi.get<RevenueCombinedEnvelope>(
        `/reports/bi/revenue-combined/customer/${customerId}?${buildQuery(merged)}`,
      );
      return res.data;
    },
  });
}

/** Download revenue-combined as XLSX blob — triggers browser save. */
export async function exportCustomerRevenueCombined(
  customerId: number,
  params: Pick<RevenueCombinedParams, 'gte' | 'lte'>,
): Promise<void> {
  const query = buildQuery({ ...params, export: 'excel' });
  const res = await dataCentralApi.get(
    `/reports/bi/revenue-combined/customer/${customerId}?${query}`,
    { responseType: 'blob' },
  );
  const url = URL.createObjectURL(new Blob([res.data as BlobPart]));
  const a = document.createElement('a');
  a.href = url;
  a.download = `revenue-allocation-customer-${customerId}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
